import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Industry images
const industries = [
    {
        id: "rakentaminen",
        title: "Rakentaminen",
        image: "https://images.pexels.com/photos/29288255/pexels-photo-29288255.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        description: "Tilitoimistopalvelut rakennusyrittäjälle"
    },
    {
        id: "auto",
        title: "Auto- ja konekauppa",
        image: "https://images.unsplash.com/photo-1619642737579-a7474bee1044?crop=entropy&cs=srgb&fm=jpg&q=85",
        description: "Autoalan kirjanpito ja taloushallinto"
    },
    {
        id: "kauppa",
        title: "Kauppa",
        image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
        description: "Vähittäiskaupan taloushallinto"
    },
    {
        id: "kuljetus",
        title: "Kuljetus",
        image: "https://images.pexels.com/photos/33897865/pexels-photo-33897865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        description: "Kuljetusalan kirjanpito ja verotus"
    },
    {
        id: "konsultointi",
        title: "Suunnittelu- ja konsultointitoiminta",
        image: "https://images.pexels.com/photos/36733331/pexels-photo-36733331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        description: "Asiantuntijapalveluiden taloushallinto"
    },
    {
        id: "ravintola",
        title: "Ravintola",
        image: "https://images.unsplash.com/photo-1669944734760-cf3279e219f0?crop=entropy&cs=srgb&fm=jpg&q=85",
        description: "Ravintola-alan kirjanpito"
    },
    {
        id: "hoiva",
        title: "Hoiva",
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80",
        description: "Hoiva-alan taloushallinto"
    },
    {
        id: "palvelut",
        title: "Palveluyritykset",
        image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80",
        description: "Palveluyritysten kirjanpito"
    }
];

const Toimialaratkaisut = () => {
    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="bg-[#1a1a1a] text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
                            Asiantuntevat tiimit jokaiselle toimialalle:
                        </h1>
                        <p className="text-lg text-white/80 max-w-3xl mx-auto">
                            Katso, mitä oman toimialasi asiantuntijuus tarkoittaa käytännössä – ja miten se hyödyttää yritystäsi.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Industries Grid */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {industries.map((industry, index) => (
                            <motion.div
                                key={industry.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Link 
                                    to={`/toimialat/${industry.id}`}
                                    className="group block relative h-[280px] rounded-2xl overflow-hidden"
                                    data-testid={`industry-card-${industry.id}`}
                                >
                                    {/* Background Image */}
                                    <div 
                                        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                                        style={{ backgroundImage: `url(${industry.image})` }}
                                    />
                                    
                                    {/* Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a]/40 to-transparent" />
                                    
                                    {/* Content */}
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <h3 className="text-xl font-semibold text-white mb-4">
                                            {industry.title}
                                        </h3>
                                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white group-hover:text-[#1a1a1a] transition-all">
                                            <ArrowRight className="h-5 w-5 text-white group-hover:text-[#1a1a1a]" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">
                            Etkö löytänyt toimialaasi?
                        </h2>
                        <p className="text-slate-600 mb-8">
                            Palvelemme myös muita toimialoja. Ota yhteyttä ja kerro tarpeistasi.
                        </p>
                        <Link 
                            to="/#yhteystiedot"
                            className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-8 py-3 rounded-full font-medium hover:bg-[#000000] transition-colors"
                        >
                            Ota yhteyttä
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Toimialaratkaisut;
